import React, { useRef, useState } from 'react'
import type { PopperChildrenProps, ReferenceChildrenProps } from 'react-popper'
import { Manager, Popper, Reference } from 'react-popper'

import {
  Button,
  ButtonClean,
  ButtonGroup,
  Fieldset,
  Tag,
  useOutsideClick,
} from '@fourthwall/components'
import { ArrowShortDownIcon } from '@fourthwall/icons'
import { useFormik, useFormikContext } from '@fourthwall/utils/dist/formik'
import { TimezonedDatePicker } from '@modules/Timezone/components/TimezonedDatePicker'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import type { FormValues } from '../../types'
import * as S from './styled'

export type Props = {
  onDateUpdate: (newDate: Date) => void
}

export const LiveStreamScheduleTag = ({ onDateUpdate }: Props) => {
  const [isOpen, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { values } = useFormikContext<FormValues>()

  useOutsideClick(containerRef, () => {
    setOpen(false)
  })

  const toggleOpen = () => {
    setOpen((prevIsOpen) => !prevIsOpen)
  }

  const formik = useFormik<{ date: Date }>({
    initialValues: {
      date: new Date(values.liveStreamStartDate!),
    },
    onSubmit: (values) => {
      onDateUpdate(values.date)
      setOpen(false)
    },
    enableReinitialize: true,
  })

  const renderReference = ({ ref }: ReferenceChildrenProps) => {
    return (
      <button ref={ref} type="button" onClick={toggleOpen}>
        <Tag
          label={
            <TimezonedDateString
              date={values.liveStreamStartDate!}
              format="[Stream starts] MMM D"
              withTimezoneAbbreviation={false}
            />
          }
          appearance="success"
          size="large"
          icon={ArrowShortDownIcon}
        />
      </button>
    )
  }

  const renderTooltip = ({ ref, style }: PopperChildrenProps) => (
    <div ref={ref} style={{ ...style, paddingTop: 8, zIndex: 5 }}>
      <S.FormContainer>
        <form onSubmit={formik.handleSubmit} data-testid="ScheduleTag.form">
          <Fieldset>
            <Fieldset.Row>
              <TimezonedDatePicker
                value={[formik.values.date]}
                labels={['Publish date & time']}
                dateFormat="MMM D, YYYY, h:mm A"
                minDate={new Date()}
                onChange={([newDate]) => {
                  formik.setFieldValue('date', newDate)
                }}
                showTimeInputs
              />
            </Fieldset.Row>
          </Fieldset>
          <S.ButtonsContainer>
            <ButtonGroup justify="right" align="center" space="medium">
              <ButtonClean
                label="Start now"
                onClick={() => {
                  formik.setFieldValue('date', new Date())
                  formik.submitForm()
                }}
              />
              <Button
                label="Cancel"
                size="small"
                onClick={() => {
                  formik.resetForm()
                  setOpen(false)
                }}
              />
              <Button
                label="Save"
                appearance="primary"
                disabled={!formik.dirty || !formik.isValid}
                size="small"
                onClick={formik.submitForm}
              />
            </ButtonGroup>
          </S.ButtonsContainer>
        </form>
      </S.FormContainer>
    </div>
  )

  return (
    <Manager>
      <div ref={containerRef}>
        <Reference>{renderReference}</Reference>
        {isOpen && <Popper placement="bottom-end">{renderTooltip}</Popper>}
      </div>
    </Manager>
  )
}
